import React, { useEffect, useState } from "react";
import { Dialog, Slide } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { setLoading } from "../../../../stores/MainStore/MainStore";
import { getLicensePlateInfos, getWashingStatus } from "../../../../services/operatorService";
import { AppError } from "../../../../services/classes";
import { Ticket } from "../../../../types/carWashing.types";
import { VehicleInfo, WashingStatus } from "../../../../types/vehicle.types";
import VehicleItem from "../VehicleItem/VehicleItem";
import FleetCard from "./FleetCard";
import TicketCard from "./TicketCard";
import {
  TicketWrapper,
  DialogContainer,
  NoTickets,
  ReturnButton,
  TicketsContainer,
  StatusBanner,
  WashingStatusList,
  WashingStatusItem,
  WashingStatusLabel,
} from "./VehicleModalStyles";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface Props {
  activePlate: string;
  numberOfMachines: number;
  setActivePlate: (activePlate: string) => void;
}

const VehicleModal: React.FC<Props> = ({
  activePlate,
  numberOfMachines,
  setActivePlate,
}: Props) => {
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo>({
    tickets: [],
    clients: [],
    fleets: [],
    statuses: [],
  });
  const [washingStatuses, setWashingStatuses] = useState<WashingStatus[]>([]);

  useEffect(() => {
    getPlateInfos();
    fetchWashingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateTickets = (updatedTicket: Ticket) => {
    const ticketList = vehicleInfo.tickets.map((ticket) => {
      if (ticket.id === updatedTicket.id) return updatedTicket;

      return ticket;
    });

    setVehicleInfo({
      ...vehicleInfo,
      tickets: ticketList,
    });
  };

  const fetchWashingStatus = () => {
    getWashingStatus(activePlate)
      .then(setWashingStatuses)
      .catch(() => setWashingStatuses([]));
  };

  const getPlateInfos = () => {
    setLoading(true);
    getLicensePlateInfos(activePlate)
      .then((res) => {
        //ordenar tickets por available e createdAt = true primeiro, depois false, depois os mais recentes
        const orderedTickets = res.tickets.sort((a, b) => {
          if (a.available !== b.available) {
            return a.available ? -1 : 1;
          }

          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        });

        setVehicleInfo({
          ...res,
          tickets: orderedTickets,
        });
      })
      .catch((err: AppError) => {
        console.log(err.message);
        toast.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    setActivePlate("");
  };

  const renderTickets = () =>
    vehicleInfo.tickets?.map((ticket) => (
      <TicketWrapper key={ticket.id}>
        <TicketCard
          ticket={ticket}
          numberOfMachines={numberOfMachines}
          updateList={getPlateInfos}
          updateTickets={updateTickets}
        />
      </TicketWrapper>
    ));

  const hasContent =
    vehicleInfo.fleets?.length > 0 ||
    vehicleInfo.clients?.length > 0 ||
    vehicleInfo.tickets?.length > 0;

  const activeStatus = vehicleInfo.statuses?.find((s) => s !== null) ?? null;
  const statusMessage = activeStatus == null
    ? "Nenhum status disponível"
    : typeof activeStatus === "string"
      ? activeStatus
      : activeStatus?.message ?? activeStatus?.description ?? JSON.stringify(activeStatus);

  return (
    <Dialog
      fullScreen
      open={!!activePlate}
      onClose={handleClose}
      slots={{
        transition: Transition,
      }}
    >
      <DialogContainer>
        <VehicleItem plate={activePlate} showStatus={false} />
        <StatusBanner>{statusMessage}</StatusBanner>
        {hasContent ? (
          <TicketsContainer>
            {vehicleInfo.fleets?.length > 0
              ? vehicleInfo.fleets.map((fleet) => (
                  <TicketWrapper key={fleet.id}>
                    <FleetCard fleet={fleet} numberOfMachines={numberOfMachines} plate={activePlate} updateList={getPlateInfos} />
                  </TicketWrapper>
                ))
              : vehicleInfo.clients?.map((client) => (
                  <TicketWrapper key={client.id}>
                    <FleetCard client={client} numberOfMachines={numberOfMachines} plate={activePlate} updateList={getPlateInfos} />
                  </TicketWrapper>
                ))}
            {renderTickets()}
          </TicketsContainer>
        ) : (
          <NoTickets>Nenhum ticket encontrado</NoTickets>
        )}
        {washingStatuses.length > 0 && (
          <>
            <WashingStatusLabel>Últimos status</WashingStatusLabel>
            <WashingStatusList>
              {washingStatuses.map((s, i) => (
                <WashingStatusItem key={i}>{s.message}</WashingStatusItem>
              ))}
            </WashingStatusList>
          </>
        )}
        <ReturnButton onClick={handleClose}>
          <ArrowBackIcon fontSize="large" />
          VOLTAR
        </ReturnButton>
      </DialogContainer>
    </Dialog>
  );
};

export default VehicleModal;
